import firebase from 'firebase/app';
import 'firebase/auth';
import 'firebase/analytics';
import 'firebase/firestore';
import 'firebase/storage';
import Request from "../models/request";
import {COLLECTIONS} from "./db";
import User from "../models/user";
import axios from 'axios';
import {blogs, HOST} from "./constants";
import Recipe from "../models/recipe";
import recipeToHTML from "./recipe-to-html";
import Post, { PostContent, Table } from '../models/post';
import postContentToHTML, {sourcesToHTML} from './post-content-to-html';
import {nanoid} from "nanoid";

const firebaseConfig = {
    apiKey: "AIzaSyBP1L8a45GPhMAMqjxabfQns3FYGFDFLHE",
    authDomain: "bitterliebe-cms.firebaseapp.com",
    projectId: "bitterliebe-cms",
    storageBucket: "bitterliebe-cms.appspot.com",
    messagingSenderId: "258782594507",
    appId: "1:258782594507:web:dec8d31f81356c4746624e",
    measurementId: "G-3W56EYZ4QQ"
};

class Firebase {
    private static instance = new Firebase();

    public static getInstance() {
        return this.instance;
    }

    auth: firebase.auth.Auth;
    analytics: firebase.analytics.Analytics;
    db: firebase.firestore.Firestore;
    storage: firebase.storage.Storage;

    constructor() {
        firebase.initializeApp(firebaseConfig);

        this.auth = firebase.auth();
        this.analytics = firebase.analytics();
        this.db = firebase.firestore();
        this.storage = firebase.storage();
    }

    // saves account request in database
    async requestAccount(user: Request) {
        const docs = await this.db.collection(COLLECTIONS.REQUESTS).where("email", "==", user.email).get();
        if (!docs.empty) return true;
        return this.db.collection(COLLECTIONS.REQUESTS).add(user);
    }

    // accepts account request by sending a request to the backend, where the firebase admin sdk is used to create a new user
    // possible security concern - passwords have to be stored in database without a hash until the request was approved
    async acceptRequest(request: Request, permissions: string) {
        const res = await axios.post(HOST + "/createFirebaseUser", {
            email: request.email,
            password: request.password,
        });
        if (!res.data) return;
        const id = (res.data as { id: string }).id;

        const dbUser: User = {
            firstName: request.firstName,
            lastName: request.lastName,
            email: request.email,
            admin: false,
            permissions
        }

        await this.db.collection(COLLECTIONS.USERS).doc(id).set(dbUser);

        await this.deleteRequest(request);
    }

    // if the request is declined, it's deleted from the database
    async deleteRequest(request: Request) {
        (await this.db.collection(COLLECTIONS.REQUESTS).where("email", "==", request.email).get())
            .forEach(d => d.ref.delete());
    }

    getRequestCollection = () => this.db.collection(COLLECTIONS.REQUESTS);
    getRequests = () => this.db.collection(COLLECTIONS.REQUESTS);
    getUserForId = (id: string) => this.db.collection(COLLECTIONS.USERS).doc(id);
    getUsers = () => this.db.collection(COLLECTIONS.USERS);
    signIn = (email: string, password: string) => this.auth.signInWithEmailAndPassword(email, password);
    signOut = () => this.auth.signOut();

    // converts file to base64
    toBase64 = (file: File) => new Promise<string | ArrayBuffer>((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result!);
        reader.onerror = error => reject(error);
    });

    getRecipes = () => this.db.collection(COLLECTIONS.RECIPES);

    // uploads a recipe to the server, and to shopify by making a request to the /create-recipe endpoint
    async uploadRecipe(recipe: Recipe, file: File, user: User) {
        //await this.storage.ref("recipe_images/" + recipe.id).put(file);
        recipe.image = (await this.toBase64(file)) as string;
        const html = recipeToHTML(recipe);
        const res = await axios.post(HOST + "/create-recipe", {
            title: recipe.title,
            excerpt: "" + recipe.duration,
            tag: recipe.category,
            author: user.firstName + " " + user.lastName,
            html,
            image: await this.toBase64(file)
        });

        const {de, ch} = res.data as { de: number; ch: number };
        if (!de || !ch) return false;

        recipe.blogId.de = de;
        recipe.blogId.ch = ch;

        await this.db.collection(COLLECTIONS.RECIPES).doc(recipe.id).set(recipe);

        return true;
    }

    // updates recipe in the database, and on shopify by making a request to the /update-recipe endpoint
    async updateRecipe(recipe: Recipe, file: File | undefined, user: User) {
        if (!!file) {
            const task = await this.storage.ref("recipe_images/" + recipe.id).put(file);
            recipe.image = await task.ref.getDownloadURL();
        }

        const html = recipeToHTML(recipe);

        const res = await axios.post(HOST + "/update-recipe", {
            recipe,
            html,
            author: user.firstName + " " + user.lastName,
            image: !file ? null : await this.toBase64(file)
        });

        const {success} = res.data as { success: boolean };

        if (!success) return false;

        await this.db.collection(COLLECTIONS.RECIPES).doc(recipe.id).set(recipe);

        return true;
    }

    // deletes a recipe from the database and shopify
    async deleteRecipe(recipe: Recipe) {
        await axios.post(HOST + "/delete-recipe", {
            recipe
        });
        await this.db.collection(COLLECTIONS.RECIPES).doc(recipe.id).delete();
    }

    getPages = () => this.db.collection(COLLECTIONS.PAGES);

    getPosts = (blog: string) => this.db.collection(blog);

    async uploadBlogPost(post: Post, image: File) {
        let html = postContentToHTML(post.content);
        if(post.sources) {
            html += sourcesToHTML(post.sources);
        }

        const task = await this.storage.ref("recipe_images/" + post.id).put(image);
        post.image = await task.ref.getDownloadURL();
        const blogId = blogs.find(b => b.handle === post.blog)!.id;

        const res = await axios.post(HOST + "/create-blog", {
            title: post.title,
            excerpt: post.excerpt,
            tag: post.time+"_min",
            author: post.author,
            published: post.published,
            html,
            image: await this.toBase64(image),
            id: blogId
        });

        const {id, handle} = res.data as {id: number, handle: string};
        post.postId = id;
        post.handle = handle;
        console.log(post, html);

        await this.db.collection(post.blog).doc(post.id).set(post);
    }
    async updateBlogPost(post: Post, image: File, updatedImage: boolean) {
        let html = postContentToHTML(post.content);
        if(post.sources) {
            html += sourcesToHTML(post.sources);
        }
        if (updatedImage) {
            const task = await this.storage.ref("recipe_images/" + post.id).put(image);
            post.image = await task.ref.getDownloadURL();
        }
        const blogId = blogs.find(b => b.handle === post.blog)!.id;

        const res = await axios.put(HOST + "/update-blog", {
            postId: post.postId,
            title: post.title,
            excerpt: post.excerpt,
            tag: post.time+"_min",
            author: post.author,
            published: post.published,
            banner: {...post.banner, text: post.banner!.text.replaceAll('"', '___')},
            html,
            image: await this.toBase64(image),
            id: blogId
        });

        const {success, metaId} = res.data as {success: boolean, metaId: number};
        if (!success) return;

        post.banner!.id = metaId;

        post.content = post.content.map(m => {
            if (m.type === 'table') {
                return {...m, value: {...m.value, columns: (m.value as Table).columns.map((o, i) => ({[i]: o}))}} as PostContent;
            } else {
                return m;
            }
        });

        console.log(post.content);
        

        await this.db.collection(post.blog).doc(post.id).set(post);
    }

    async deletePost(post: Post) {
        const blogId = blogs.find(b => b.handle === post.blog)!.id;
        const res = await axios.delete(HOST+`/delete-blog?id=${post.postId}&blog=${blogId}`);

        const {success} = res.data as {success: boolean};
        if (!success) return;

        await this.db.collection(post.blog).doc(post.id).delete();
    }

    // creates a post in the database, and on shopify by calling the /create-post endpoint
    async createPost(title: string, blog: string, author: string) {

        const blogId = blogs.find(b => b.handle === blog)!.id;
        const res = await axios.post(HOST + '/create-post', {
            title,
            blogId,
            author
        });


        const {id, handle, metaId} = res.data as {id: number, handle: string, metaId: number};

        const post: Post = {
            id: nanoid(6),
            author,
            postId: id,
            excerpt: "",
            blog,
            handle,
            title,
            content: [],
            published: false,
            sources: [],
            banner: {id: metaId, text: "", color: "#FF7F7D"},
            image: "",
            time: "",
        };

        await this.db.collection(blog).doc(post.id).set(post);

        return post.id;
    }
}


export default Firebase;
